// File: app/src/utils/sessionFormat.js
//
// "What you train" and "how the session runs" as two separate choices (#83,
// L4). The old single style list mixed adaptations (strength, hypertrophy,
// conditioning) with formats (circuit, EMOM, AMRAP, Tabata, superset), so
// "strength as an EMOM" was impossible to ask for, and four formats were only
// reachable through Mixed.
//
// Storage stays backward compatible: `workoutTypes` still holds the goals (the
// same style keys as before, plus the Surprise me / What's next selectors), and
// the format is a new `format` field. Old selections that put a format in
// `workoutTypes` are moved across by normaliseStyleSelection().
//
// The prompt keeps working in style keys. resolveSessionFormat() turns
// goals + format into the styles the prompt builds from:
//   straight / superset  the goals' own specs, with the session's rest style
//                        forced to full or paired (the paired-rest rule in
//                        trainingPreferencesPrompt does the superset work)
//   circuit / emom /     the format's spec, with the goals passed separately
//   amrap / tabata       so the prompt can say how to load inside it
//   auto                 the goals' own specs and the user's preferences —
//                        except No faff, which picks EMOM (strength/power) or
//                        circuit, because straight sets mean rest and kit swaps

import { SELECTOR_STYLES } from "./mixedStyle";

export const FORMATS = [
  "auto",
  "straight",
  "superset",
  "circuit",
  "emom",
  "amrap",
  "tabata",
];

// Format -> the prompt style that describes its structure.
const FORMAT_STYLE = {
  superset: "superset",
  circuit: "circuit",
  emom: "emom",
  amrap: "amrap",
  tabata: "tabata",
};

// Goals that come from the old list but are really formats.
const LEGACY_FORMAT_STYLES = {
  circuit: "circuit",
  functional: "circuit",
  emom: "emom",
  amrap: "amrap",
  tabata: "tabata",
  superset: "superset",
};

const RESISTANCE_GOALS = ["strength", "hypertrophy", "power"];

// A format with nothing to train defaults to conditioning — the most common
// reason to ask for a circuit or an EMOM without saying what for.
const DEFAULT_FORMAT_GOAL = "conditioning";

const lower = (list) =>
  (Array.isArray(list) ? list : list ? [list] : [])
    .filter((s) => typeof s === "string" && s.trim())
    .map((s) => s.trim().toLowerCase());

/**
 * Move format-type entries out of a legacy `workoutTypes` list into `format`.
 * Returns `{ workoutTypes, format }`; a list with nothing legacy comes back
 * unchanged apart from defaulting the format to "auto".
 */
export const normaliseStyleSelection = ({ workoutTypes, format } = {}) => {
  const styles = lower(workoutTypes);
  const legacy = styles.filter((s) => LEGACY_FORMAT_STYLES[s]);
  let goals = styles.filter((s) => !LEGACY_FORMAT_STYLES[s]);
  let resolved = FORMATS.includes(format) ? format : "auto";
  if (legacy.length > 0) {
    if (resolved === "auto") resolved = LEGACY_FORMAT_STYLES[legacy[0]];
    if (goals.length === 0) goals = [DEFAULT_FORMAT_GOAL];
  }
  return { workoutTypes: goals, format: resolved };
};

/** True when the selection asks Khronos to choose the shape. */
export const hasSelector = (workoutTypes) => {
  const styles = lower(workoutTypes);
  return styles.length === 0 || styles.some((s) => SELECTOR_STYLES.includes(s));
};

/**
 * What format Auto lands on, for display and for resolveSessionFormat.
 * Returns a FORMATS value other than "auto", or null for "each goal's own".
 */
export const autoFormatFor = ({ goals, noFaff }) => {
  const list = lower(goals);
  if (!noFaff) return null;
  if (list.some((g) => g === "strength" || g === "power")) return "emom";
  if (
    list.some((g) =>
      [...RESISTANCE_GOALS, "hiit", "conditioning"].includes(g),
    )
  ) {
    return "circuit";
  }
  return null;
};

/**
 * Turn goals + format into prompt styles.
 *
 * @param {Object} args
 * @param {string[]} args.goals   Concrete goal styles (selectors already resolved).
 * @param {string}  [args.format] FORMATS value.
 * @param {boolean} [args.noFaff]
 * @param {boolean} [args.selectorUsed] A selector chose the shape — its own
 *   format stands, so the format choice is ignored.
 * @returns {{ styles: string[], format: string, formatStyle: string|null,
 *             formatGoals: string[], restStyleOverride: string|null }}
 */
export const resolveSessionFormat = ({
  goals,
  format,
  noFaff = false,
  selectorUsed = false,
}) => {
  const list = lower(goals);
  const passthrough = {
    styles: list,
    format: "auto",
    formatStyle: null,
    formatGoals: [],
    restStyleOverride: null,
  };
  if (selectorUsed) return passthrough;

  let chosen = FORMATS.includes(format) ? format : "auto";
  if (chosen === "auto") {
    chosen = autoFormatFor({ goals: list, noFaff }) || "auto";
    if (chosen === "auto") return passthrough;
  }

  if (chosen === "straight") {
    return { ...passthrough, format: chosen, restStyleOverride: "full" };
  }

  if (chosen === "superset") {
    const hasResistance = list.some((g) => RESISTANCE_GOALS.includes(g));
    return hasResistance
      ? { ...passthrough, format: chosen, restStyleOverride: "paired" }
      : { ...passthrough, styles: [...list, "superset"], format: chosen };
  }

  const formatStyle = FORMAT_STYLE[chosen];
  return {
    styles: [formatStyle],
    format: chosen,
    formatStyle,
    formatGoals: list.length ? list : [DEFAULT_FORMAT_GOAL],
    restStyleOverride: null,
  };
};
